import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { SeasonEligibility } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { BodyS, HeadlineXS, LabelM } from '@sorare/core/src/atoms/typography';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { formatScarcity } from '@sorare/core/src/lib/cards';
import { seasonEligibilityMessages } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CompetitionLogo } from '#src/components/GenericCardPage/CompetitionsEligibility/CompetitionLogo/index.tsx';
import { useCompetitionsEligibility } from '#src/components/GenericCardPage/CompetitionsEligibility/useCompetitionsEligibility.tsx';

import type { EligibilityTooltip_anyCard } from './__generated__/index.graphql.ts';

const CompetitionRoot = styled(Horizontal)`
  padding: var(--unit) var(--double-unit);
  border-radius: var(--unit);
  background-color: var(--c-nd-50);
  @media ${laptopAndAbove} {
    background-color: var(--c-nd-100);
  }
`;

const TooltipRoot = styled(Vertical)`
  max-width: 400px;
`;

const Competitions = styled(Horizontal)`
  flex-wrap: wrap;
`;

const DropdownContent = styled.div`
  padding: var(--double-unit);
`;

export const EligibilityTooltip = ({
  card,
}: {
  card: EligibilityTooltip_anyCard;
}) => {
  const isLaptopAndAbove = useIsLaptop();
  const { inSeasonLeaderboards, classicCompetitions } =
    useCompetitionsEligibility<EligibilityTooltip_anyCard>(card);

  const label = (
    <Horizontal>
      <FontAwesomeIcon icon={faInfoCircle} />{' '}
      <FormattedMessage
        id="CardOverview.competitions"
        defaultMessage="{nbCompetitions, plural, =0 {# competition} one {# competition} other {# competitions}}"
        values={{
          nbCompetitions:
            inSeasonLeaderboards.length + classicCompetitions.length,
        }}
      />
    </Horizontal>
  );

  const content = (
    <TooltipRoot>
      <HeadlineXS as="h3">
        <FormattedMessage
          id="CompetitionsEligibility.title"
          defaultMessage="Competitions eligibility"
        />
      </HeadlineXS>
      <BodyS as="h6">
        <FormattedMessage
          {...seasonEligibilityMessages[SeasonEligibility.IN_SEASON]}
        />
      </BodyS>
      {inSeasonLeaderboards.length > 0 ? (
        <Competitions>
          {inSeasonLeaderboards.map(so5Leaderboard => (
            <CompetitionRoot key={so5Leaderboard.slug}>
              <CompetitionLogo size={2} so5Leaderboard={so5Leaderboard} />
              <LabelM>
                {`${so5Leaderboard.so5League.displayName} - ${
                  so5Leaderboard.mainRarityType &&
                  formatScarcity(so5Leaderboard.mainRarityType)
                }`}
              </LabelM>
            </CompetitionRoot>
          ))}
        </Competitions>
      ) : (
        <BodyS as="p" color="var(--c-nd-700)">
          <FormattedMessage
            id="CompetitionsEligibility.max1classicPerTeam"
            defaultMessage="Max 1 Classic Card per team"
          />
        </BodyS>
      )}
      <BodyS as="h6">
        <FormattedMessage
          {...seasonEligibilityMessages[SeasonEligibility.CLASSIC]}
        />
      </BodyS>
      {classicCompetitions.length > 0 ? (
        <Competitions>
          {classicCompetitions.map(so5Leaderboard => (
            <CompetitionRoot key={so5Leaderboard.slug}>
              <CompetitionLogo size={2} so5Leaderboard={so5Leaderboard} />
              <LabelM>
                {`${so5Leaderboard.so5League.displayName} - ${
                  so5Leaderboard.mainRarityType &&
                  formatScarcity(so5Leaderboard.mainRarityType)
                }`}
              </LabelM>
            </CompetitionRoot>
          ))}
        </Competitions>
      ) : (
        <BodyS as="p" color="var(--c-nd-700)">
          <FormattedMessage
            id="CompetitionsEligibility.notEligible"
            defaultMessage="Not eligible"
          />
        </BodyS>
      )}
    </TooltipRoot>
  );

  if (isLaptopAndAbove) {
    return <Tooltip title={content}>{label}</Tooltip>;
  }
  return (
    <Dropdown label={props => <ButtonBase {...props}>{label}</ButtonBase>}>
      <DropdownContent>{content}</DropdownContent>
    </Dropdown>
  );
};

EligibilityTooltip.fragments = {
  anyCard: gql`
    fragment EligibilityTooltip_anyCard on AnyCardInterface {
      slug
      sport
      eligibleUpcomingLeagueTracks {
        slug
        entrySo5Leaderboard {
          slug
          ...CompetitionLogo_so5Leaderboard
        }
      }
      ...useCompetitionsEligibility_anyCard
    }
    ${useCompetitionsEligibility.fragments.anyCard}
    ${CompetitionLogo.fragments.so5Leaderboard}
  ` as TypedDocumentNode<EligibilityTooltip_anyCard>,
};
